import { useState, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import DashboardLayout from "../components/DashboardLayout";

// ─── Utility helpers ────────────────────────────────────────────────────────
const timeToMinutes = (t) => {
  if (!t || typeof t !== "string" || !t.includes(":")) return 0;
  const parts = t.split(":");
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return 0;
  return h * 60 + m;
};
const minutesToTime = (m) => {
  if (typeof m !== "number" || isNaN(m) || m < 0) return "00:00";
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};
const todayISO = () => new Date().toISOString().split("T")[0];

// Convert API date to YYYY-MM-DD without changing the calendar date.
const toDateInputValue = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};




// Add calendar days without timezone-related date shifts.
const addCalendarDays = (dateString, daysToAdd) => {
  const [year, month, day] = dateString.split("-").map(Number);

  const date = new Date(Date.UTC(year, month - 1, day + daysToAdd));

  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

// Convert 24-hour time (14:30) to 12-hour time (2:30 PM).
const formatTime12Hour = (time) => {
  if (!time || typeof time !== "string") return "";

  const match = time.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return time;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (hours > 23 || minutes > 59) return time;

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;

  return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
};
const uniqueId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const escapeHtml = (s) =>
  String(s ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
      c
      ],
  );

const formatLongDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

// Talk topic comes from speaker.talk_details["Talk Topic"]
const getTalkTopic = (sp) => {
  const t = sp?.talk_details?.["Talk Topic"];
  return typeof t === "string" && t.trim() ? t.trim() : "";
};

// Builds a print-ready HTML document of the final schedule (used for PDF export)
function buildScheduleHtml(
  days,
  speakers,
  eventName,
  eventLogo,
  eventStartDate,
  eventEndDate
) {
  const dayBlocks = days
    .map((day) => {
      const rows = [...day.schedule]
        .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))
        .map((task) => {
          const sp = task.speakerId
            ? speakers.find((s) => s.id === task.speakerId)
            : null;
          const end = minutesToTime(
            timeToMinutes(task.startTime) + task.duration,
          );
          const isSpeaker = task.type === "speaker";

          let subParts = [];
          if (sp) {
            if (sp.name !== task.name) subParts.push(sp.name);
            if (sp.jobtitle) subParts.push(sp.jobtitle);
            if (sp.company) subParts.push(sp.company);
          }
          const sub = subParts.length
            ? `<div class="sub">${subParts.map(escapeHtml).join(" · ")}</div>`
            : "";
          const topic = sp ? getTalkTopic(sp) : "";
          const topicHtml = topic
            ? `<div class="topic">Topic: ${escapeHtml(topic)}</div>`
            : "";

            // Speaker profile image for PDF export
const rawPhoto = sp?.profile_picture?.base64;

const photoSrc = rawPhoto
  ? rawPhoto.startsWith("data:image/")
    ? rawPhoto
    : `data:image/jpeg;base64,${rawPhoto}`
  : "";

const speakerPhotoHtml = photoSrc
  ? `<img
       class="speaker-photo"
       src="${escapeHtml(photoSrc)}"
       alt="${escapeHtml(sp?.name || "Speaker")}"
     />`
  : "";

          return `
          <tr class="${isSpeaker ? "sp" : "br"}">
            <td class="time">${escapeHtml(task.startTime)} – ${escapeHtml(end)}</td>
            <td class="session">
  <div class="speaker-session">
    ${speakerPhotoHtml}

    <div class="speaker-session-content">
      <div class="name">${escapeHtml(task.name)}</div>
      ${sub}
      ${topicHtml}
    </div>
  </div>
</td>
            <td class="room">${task.room ? "📍 " + escapeHtml(task.room) : "—"}</td>
            <td class="dur">${task.duration} min</td>
          </tr>`;
        })
        .join("");

      return `
      <section class="day">
        <div class="day-head">
          <h2>${escapeHtml(day.title)}</h2>
          <span>${escapeHtml(formatLongDate(day.date))} · ${escapeHtml(day.startTime)} – ${escapeHtml(day.endTime)}</span>
        </div>
        <table>
          <thead>
            <tr><th style="width:130px">Time</th><th>Session</th><th style="width:150px">Room</th><th style="width:70px">Duration</th></tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </section>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Event Schedule</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body {
    font-family: 'Segoe UI', system-ui, -apple-system, Arial, sans-serif;
    color: #1a1a2e; margin: 0; font-size: 12px;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .top {
    background: #2D2856; color: #fff; padding: 16px 20px; border-radius: 6px; margin-bottom: 18px;
  }
    .pdf-event-brand {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pdf-event-logo {
  width: 64px;
  height: 64px;
  object-fit: contain;
  background: #ffffff;
  border-radius: 8px;
  padding: 5px;
  flex-shrink: 0;
}

.pdf-event-details {
  min-width: 0;
}

.pdf-event-details h1 {
  margin: 0;
  font-size: 22px;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.pdf-event-details p {
  margin: 5px 0 0;
  font-size: 11px;
  opacity: 0.85;
}

.pdf-event-details .pdf-event-dates {
  font-size: 13px;
  font-weight: 600;
  opacity: 1;
  color: #e2dcff;
}

@media print {
  .top {
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }

  .pdf-event-brand {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
  .top h1 { margin: 0; font-size: 22px; }
  .top p { margin: 4px 0 0; opacity: .8; font-size: 12px; }
  .day { margin-bottom: 22px; }
  .day-head {
    display: flex; justify-content: space-between; align-items: baseline;
    border-bottom: 2px solid #2D2856; padding-bottom: 6px; margin-bottom: 8px;
    break-after: avoid; page-break-after: avoid;
  }
  .day-head h2 { margin: 0; font-size: 16px; color: #2D2856; }
  .day-head span { color: #64748b; font-size: 12px; }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }
  th {
    text-align: left; font-size: 11px; color: #64748b; font-weight: 600;
    padding: 6px 8px; background: #f1f5f9;
  }
  td { padding: 7px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  tr.sp td:first-child { border-left: 4px solid #2D2856; }
  tr.br td:first-child { border-left: 4px solid #f97316; }
  .time { font-weight: 700; color: #2D2856; white-space: nowrap; }
  .name { font-weight: 600; }

  .speaker-session {
  display: flex;
  align-items: flex-start;
  gap: 9px;
}

.speaker-photo {
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #e2e8f0;
  background: #f1f5f9;
  flex-shrink: 0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.speaker-session-content {
  min-width: 0;
  flex: 1;
}
  .sub { color: #64748b; font-size: 11px; margin-top: 2px; }
  .topic { color: #2D2856; font-size: 11px; font-weight: 600; margin-top: 3px; }
  .room { color: #065f46; }
  .dur { color: #64748b; white-space: nowrap; }
  .foot { margin-top: 14px; color: #94a3b8; font-size: 10px; text-align: right; }
</style>
</head>
<body>
 <div class="top">
  <div class="pdf-event-brand">
    ${
      eventLogo
        ? `<img
             class="pdf-event-logo"
             src="${escapeHtml(eventLogo)}"
             alt="Event logo"
           />`
        : ""
    }

    <div class="pdf-event-details">
      <h1>${escapeHtml(eventName || "Event Schedule")}</h1>

      <p class="pdf-event-dates">
        ${
          eventStartDate
            ? escapeHtml(formatLongDate(eventStartDate))
            : "Start date unavailable"
        }
        ${
          eventEndDate
            ? ` – ${escapeHtml(formatLongDate(eventEndDate))}`
            : ""
        }
      </p>

      <p>
        Event Schedule · ${days.length}
        day${days.length !== 1 ? "s" : ""} ·
        ${days.reduce((n, d) => n + d.schedule.length, 0)} sessions
      </p>
    </div>
  </div>
</div>
  ${dayBlocks}
  <div class="foot">Generated on ${escapeHtml(new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }))}</div>
</body>
</html>`;
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function Avatar({ name, photoBase64, size = 36 }) {
  const initials = name
    ? name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
    : "?";

  if (photoBase64) {
    return (
      <img
        src={photoBase64}
        alt={name}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
          border: "2px solid #e2e8f0",
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#2D2856",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontWeight: "bold",
        fontSize: size * 0.35,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

function Btn({
  children,
  onClick,
  variant = "primary",
  size = "md",
  disabled = false,
  style: sx = {},
}) {
  const base = {
    padding: size === "sm" ? "7px 13px" : "11px 20px",
    fontSize: size === "sm" ? "0.82rem" : "0.93rem",
    fontWeight: 600,
    borderRadius: 5,
    cursor: disabled ? "not-allowed" : "pointer",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "all .2s",
    opacity: disabled ? 0.5 : 1,
    fontFamily: "inherit",
  };
  const variants = {
    primary: { background: "#2D2856", color: "white" },
    secondary: { background: "#94a3b8", color: "white" },
    danger: { background: "#ef4444", color: "white" },
    outline: {
      background: "transparent",
      border: "2px solid #2D2856",
      color: "#2D2856",
    },
    success: { background: "#10b981", color: "white" },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{ ...base, ...variants[variant], ...sx }}
    >
      {children}
    </button>
  );
}

function Notification({ message }) {
  return message ? (
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        left: "20px",
        maxWidth: 360,
        margin: "0 auto",
        background: "#1e293b",
        color: "white",
        padding: "14px 22px",
        borderRadius: 5,
        boxShadow: "0 8px 25px rgba(0,0,0,.25)",
        zIndex: 9999,
        fontSize: "0.95rem",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      ✅ {message}
    </div>
  ) : null;
}

function Modal({ open, onClose, title, children, maxWidth = 800 }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: 5,
          width: "100%",
          maxWidth,
          maxHeight: "90vh",
          overflowY: "auto",
          padding: "20px 16px",
          boxShadow: "0 20px 50px rgba(0,0,0,.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid #f1f5f9",
            paddingBottom: 15,
            marginBottom: 22,
          }}
        >
          <h2 style={{ margin: 0, color: "#1a1a2e", fontSize: "1.1rem" }}>
            {title}
          </h2>
          <span
            onClick={onClose}
            style={{
              cursor: "pointer",
              fontSize: "1.6rem",
              color: "#94a3b8",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            ×
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

// start/end are real slot boundaries (slots begin exactly where the
// previous activity ends). Shown in 12-hour AM/PM format.
function DropSlot({ start, end, onDrop }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onDrop();
      }}
      style={{
        background: over ? "rgba(45,40,86,.06)" : "#f8fafc",
        border: `2px dashed ${over ? "#2D2856" : "#e2e8f0"}`,
        borderRadius: 5,
        padding: "10px 12px",
        marginBottom: 10,
        color: over ? "#2D2856" : "#94a3b8",
        fontStyle: "italic",
        fontSize: "0.85rem",
        textAlign: "center",
        transition: "all .2s",
        minHeight: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {formatTime12Hour(start)} — {formatTime12Hour(end)} ·{" "}
      {over ? "Drop here!" : "Available slot"}
    </div>
  );
}

// ─── Compact Schedule Preview ────────────────────────────────────────────────
// A dense agenda list: one row per session (time | session + speaker | room).

function TimelinePreview({
  days,
  speakers,
  previewDayIndex,
  onPreviewDayChange,
  maxHeight = 360,
}) {
  const currentDay = days[previewDayIndex];

  const formatDayLabel = (day, idx) => {
    if (!day.date) return `Day ${idx + 1}`;
    const d = new Date(day.date + "T00:00:00");
    return `Day ${idx + 1} · ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  };

  const sessions = currentDay
    ? [...currentDay.schedule]
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))
      .map((act) => {
        const sp = act.speakerId
          ? speakers.find((s) => s.id === act.speakerId)
          : null;
        return { ...act, speakerObj: sp };
      })
    : [];

  if (!days.length)
    return (
      <div
        style={{ textAlign: "center", padding: "30px 20px", color: "#94a3b8" }}
      >
        <div style={{ fontSize: "2rem", marginBottom: 8 }}>📅</div>
        <p style={{ margin: 0 }}>Create a day and add activities to see the preview.</p>
      </div>
    );

  const firstStart = sessions.length ? sessions[0].startTime : null;
  const last = sessions.length ? sessions[sessions.length - 1] : null;
  const lastEnd = last
    ? minutesToTime(timeToMinutes(last.startTime) + last.duration)
    : null;

  return (
    <div style={{ fontFamily: "inherit" }}>
      {/* Day pills + summary */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {days.map((day, i) => (
            <button
              key={day.id}
              onClick={() => onPreviewDayChange(i)}
              style={{
                padding: "5px 12px",
                borderRadius: 50,
                fontWeight: 600,
                fontSize: "0.78rem",
                cursor: "pointer",
                border: "none",
                background: i === previewDayIndex ? "#2D2856" : "#f1f5f9",
                color: i === previewDayIndex ? "white" : "#64748b",
                transition: "all .2s",
                fontFamily: "inherit",
              }}
            >
              {formatDayLabel(day, i)}
            </button>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontSize: "0.76rem",
            color: "#64748b",
            flexWrap: "wrap",
          }}
        >
          {sessions.length > 0 && (
            <span>
              <strong style={{ color: "#334155" }}>
                {sessions.length}
              </strong>{" "}
              session{sessions.length > 1 ? "s" : ""} ·{" "}
              {formatTime12Hour(firstStart)} – {formatTime12Hour(lastEnd)}
            </span>
          )}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: "#2D2856",
                display: "inline-block",
              }}
            />
            Speaker
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: "#f97316",
                display: "inline-block",
              }}
            />
            Break
          </span>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "24px 20px",
            color: "#94a3b8",
            border: "2px dashed #e2e8f0",
            borderRadius: 5,
            fontSize: "0.88rem",
          }}
        >
          No activities scheduled for this day yet.
        </div>
      ) : (
        <div
          style={{
            maxHeight,
            overflowY: "auto",
            paddingRight: 4,
          }}
        >
          {sessions.map((s) => {
            const sp = s.speakerObj;
            const end = minutesToTime(timeToMinutes(s.startTime) + s.duration);
            const sameName = sp && sp.name === s.name;
            const subtitle = sp
              ? [sp.jobtitle, sp.company].filter(Boolean).join(" · ")
              : "";
            const topic = getTalkTopic(sp);
            return (
              <div
                key={s.id}
                className="pv-row"
                style={{
                  borderLeftColor: s.type === "speaker" ? "#2D2856" : "#f97316",
                }}
              >
                {/* Time */}
                <div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#2D2856",
                      fontSize: "0.82rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatTime12Hour(s.startTime)} – {formatTime12Hour(end)}
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: "0.72rem" }}>
                    {s.duration} min
                  </div>
                </div>

                {/* Session */}
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      minWidth: 0,
                    }}
                  >
                    {sameName && (
                      <Avatar
                        name={sp.name}
                        photoBase64={sp.profile_picture?.base64}
                        size={24}
                      />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#1a1a2e",
                          fontSize: "0.88rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.name}
                      </div>
                      {sameName && subtitle && (
                        <div
                          style={{
                            color: "#64748b",
                            fontSize: "0.74rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {subtitle}
                        </div>
                      )}
                    </div>
                  </div>
                  {sp && !sameName && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 3,
                        minWidth: 0,
                      }}
                    >
                      <Avatar
                        name={sp.name}
                        photoBase64={sp.profile_picture?.base64}
                        size={18}
                      />
                      <span
                        style={{
                          color: "#64748b",
                          fontSize: "0.74rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {sp.name}
                        {subtitle ? ` · ${subtitle}` : ""}
                      </span>
                    </div>
                  )}
                  {topic && (
                    <div
                      title={topic}
                      style={{
                        marginTop: 4,
                        color: "#2D2856",
                        background: "rgba(45,40,86,.06)",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: "0.74rem",
                        fontWeight: 600,
                        display: "inline-block",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "top",
                      }}
                    >
                      💬 {topic}
                    </div>
                  )}
                </div>

                {/* Room */}
                <div className="pv-room">
                  {s.room && (
                    <span
                      style={{
                        background: "#ecfdf5",
                        color: "#065f46",
                        border: "1px solid #a7f3d0",
                        padding: "2px 9px",
                        borderRadius: 20,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      📍 {s.room}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EventScheduler() {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const [eventStartDate, setEventStartDate] = useState("");
  const [eventEndDate, setEventEndDate] = useState("");

  const [eventName, setEventName] = useState("");
const [eventLogo, setEventLogo] = useState("");
  // API data
  const [activityTemplates, setActivityTemplates] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [loadingSpeakers, setLoadingSpeakers] = useState(true);
  const [loadingSchedule, setLoadingSchedule] = useState(true);

  // Rooms (predefined by the user, reusable in any slot)
  const [rooms, setRooms] = useState([]);
  const [newRoomName, setNewRoomName] = useState("");
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [roomTargetActId, setRoomTargetActId] = useState(null);

  // Days state
  const [days, setDays] = useState([
    {
      id: uniqueId(),
      title: "Day 1",
      date: "",
      startTime: "08:00",
      endTime: "18:00",
      schedule: [],
    },
  ]);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);

  // Day form (synced from selected day)
  const [dayTitle, setDayTitle] = useState("Day 1");
  const [eventDate, setEventDate] = useState("");
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("18:00");

  // Custom activity form
  const [newActName, setNewActName] = useState("");
  const [newActDur, setNewActDur] = useState(30);

  // Modals
  const [speakerModalOpen, setSpeakerModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedActId, setSelectedActId] = useState(null);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState(null);

  // Duration editor
  const [durEditorOpen, setDurEditorOpen] = useState(false);
  const [durEditorActId, setDurEditorActId] = useState(null);
  const [durEditorDayIdx, setDurEditorDayIdx] = useState(null);
  const [durValue, setDurValue] = useState(30);

  // Preview
  const [previewDayIndex, setPreviewDayIndex] = useState(0);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);

  // Submit state
  const [submitting, setSubmitting] = useState(false);

  // Notification
  const [notification, setNotification] = useState("");
  const notifTimer = useRef(null);

  // Drag ref + hover highlight for swap target
  const dragRef = useRef(null);
  const [dragOverActId, setDragOverActId] = useState(null);

  const notify = useCallback((msg) => {
    setNotification(msg);
    clearTimeout(notifTimer.current);
    notifTimer.current = setTimeout(() => setNotification(""), 3000);
  }, []);

  const currentDay = days[currentDayIndex] ?? null;


  // Fetch event dates from the Event API.
  useEffect(() => {
    if (!eventId) return;

    const token = localStorage.getItem("token");

    fetch(`${API_BASE_URL}/event/${eventId}`, {
      headers: token
        ? { Authorization: `Bearer ${token}` }
        : {},
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch event: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        // Supports either a direct event object or a wrapped response.
       // Extract the event object from the existing API response.
       
// Extract event metadata from the API response
const event =
  data?.metadata ??
  data?.data?.metadata ??
  data?.data ??
  data;

// Event name
setEventName(
  event?.name ??
  event?.event_name ??
  event?.title ??
  ""
);

// Event logo: logo_file is outside metadata in your API response
const rawLogo =
  data?.logo_file?.base64 ??
  data?.data?.logo_file?.base64 ??
  data?.data?.metadata?.logo_file?.base64 ??
  "";

// Support both complete image data URLs and raw Base64
const logoUrl = rawLogo
  ? rawLogo.startsWith("data:image/")
    ? rawLogo
    : `data:image/png;base64,${rawLogo}`
  : "";

setEventLogo(logoUrl);

// Event dates
const startDate = toDateInputValue(event?.start_date);
const endDate = toDateInputValue(event?.end_date);
// Event name
const name =
  event?.event_name ??
  event?.name ??
  event?.title ??
  "";

setEventName(name);

// Event logo


setEventLogo(logoUrl);

        if (!startDate) {
          console.error("Event API did not return start_date:", data);
          return;
        }

        setEventStartDate(startDate);
        setEventEndDate(endDate);

        // Initialize Day 1 only when no saved schedule exists.
        setDays((previousDays) => {
          if (previousDays.length !== 1 || previousDays[0].schedule.length > 0) {
            return previousDays;
          }

          return [
            {
              ...previousDays[0],
              title: "Day 1",
              date: startDate,
            },
          ];
        });

        setEventDate(startDate);
      })
      .catch((error) => {
        console.error("Failed to load event dates:", error);
      });
  }, [eventId]);

  // ── Fetch Activities ──
  useEffect(() => {
    setLoadingActivities(true);
    fetch(`${API_BASE_URL}/getEventSchedulerBreaks`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.status === "success" && Array.isArray(data.data)) {
          setActivityTemplates(
            data.data.map((item) => ({
              id: item.id,
              name: item.name,
              duration: 30,
              type: "break",
              isCustom: false,
            })),
          );
        }
      })
      .catch((err) => console.error("Failed to load activities:", err))
      .finally(() => setLoadingActivities(false));
  }, []);

  // ── Fetch Speakers ──
  useEffect(() => {
    if (!eventId) return;
    setLoadingSpeakers(true);
    fetch(`${API_BASE_URL}/getApprovedSpeakerDetails/${eventId}`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setSpeakers(data);
        }
      })
      .catch((err) => console.error("Failed to load speakers:", err))
      .finally(() => setLoadingSpeakers(false));
  }, [eventId]);

  const activityTemplatesRef = useRef([]);
  useEffect(() => {
    activityTemplatesRef.current = activityTemplates;
  }, [activityTemplates]);

  useEffect(() => {
    if (!eventId) {
      setLoadingSchedule(false);
      return;
    }
    const token = localStorage.getItem("token");
    setLoadingSchedule(true);

    fetch(`${API_BASE_URL}/event_scheduler/${eventId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (!r.ok) {
          if (r.status === 404) return null;
          throw new Error(`HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((data) => {
        if (!data) return;

        const rawDays = data?.scheduleData?.day;
        if (!Array.isArray(rawDays) || rawDays.length === 0) return;

        const customNamesToAdd = new Set();
        const roomNamesToAdd = new Set();

        // predefined rooms saved with the schedule (strings or { name })
        const savedRooms = data?.scheduleData?.rooms;
        if (Array.isArray(savedRooms)) {
          savedRooms.forEach((r) => {
            const n = typeof r === "string" ? r : r?.name;
            if (typeof n === "string" && n.trim()) roomNamesToAdd.add(n.trim());
          });
        }

        const hydratedDays = rawDays.map((rawDay, idx) => {
          const rawStartDate =
            typeof rawDay.start_date === "string"
              ? rawDay.start_date
              : todayISO();
          const rawStartTime =
            typeof rawDay.start_time === "string" ? rawDay.start_time : "08:00";
          const rawEndTime =
            typeof rawDay.end_time === "string" ? rawDay.end_time : "18:00";

          const tasks = Array.isArray(rawDay.scheduled_task)
            ? rawDay.scheduled_task
            : [];

          const schedule = tasks
            .map((task) => {
              const taskStart =
                typeof task.start_time === "string" ? task.start_time : null;
              const taskEnd =
                typeof task.end_time === "string" ? task.end_time : null;

              if (!taskStart || !taskEnd) return null;

              const startMin = timeToMinutes(taskStart);
              const endMin = timeToMinutes(taskEnd);
              const duration = endMin > startMin ? endMin - startMin : 30;

              const activity = task.activity || {};
              const actType =
                typeof activity.type === "string" ? activity.type : "break";
              const actName =
                typeof activity.name === "string" ? activity.name : "Unknown";

              // room can live on the task or on the activity
              const rawRoom =
                typeof task.room === "string" && task.room.trim()
                  ? task.room.trim()
                  : typeof activity.room === "string" && activity.room.trim()
                    ? activity.room.trim()
                    : null;
              if (rawRoom) roomNamesToAdd.add(rawRoom);

              if (actType === "speaker") {
                const speakerId = activity.speaker_id
                  ? Number(activity.speaker_id)
                  : null;
                return {
                  id: uniqueId(),
                  templateId: null,
                  name: actName,
                  duration,
                  startTime: taskStart,
                  type: "speaker",
                  speakerId,
                  isCustom: false,
                  room: rawRoom,
                };
              }

              const knownNames = new Set(
                activityTemplatesRef.current.map((a) => a.name.toLowerCase()),
              );
              if (!knownNames.has(actName.toLowerCase())) {
                customNamesToAdd.add(actName);
              }

              return {
                id: uniqueId(),
                templateId: null,
                name: actName,
                duration,
                startTime: taskStart,
                type: "break",
                speakerId: null,
                isCustom: !knownNames.has(actName.toLowerCase()),
                room: rawRoom,
              };
            })
            .filter(Boolean);

          return {
            id: uniqueId(),
            title: `Day ${idx + 1}`,
            date: rawStartDate,
            startTime: rawStartTime,
            endTime: rawEndTime,
            schedule,
          };
        });

        const sortedDays = hydratedDays.sort((a, b) =>
          a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
        );
        const renamedDays = sortedDays.map((d, i) => ({
          ...d,
          title: `Day ${i + 1}`,
        }));

        setDays(renamedDays);
        setCurrentDayIndex(0);

        if (renamedDays.length > 0) {
          setDayTitle(renamedDays[0].title);
          setEventDate(renamedDays[0].date);
          setStartTime(renamedDays[0].startTime);
          setEndTime(renamedDays[0].endTime);
        }

        if (roomNamesToAdd.size > 0) {
          setRooms((prev) => {
            const existing = new Set(prev.map((r) => r.name.toLowerCase()));
            const toAdd = [...roomNamesToAdd]
              .filter((n) => !existing.has(n.toLowerCase()))
              .map((name) => ({ id: uniqueId(), name }));
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
          });
        }

        if (customNamesToAdd.size > 0) {
          setActivityTemplates((prev) => {
            const existingNames = new Set(
              prev.map((a) => a.name.toLowerCase()),
            );
            const toAdd = [...customNamesToAdd]
              .filter((n) => !existingNames.has(n.toLowerCase()))
              .map((name, i) => ({
                id: Date.now() + i,
                name,
                duration: 30,
                type: "break",
                isCustom: true,
              }));
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
          });
        }
      })
      .catch((err) => {
        console.warn("Could not load existing scheduler:", err.message);
      })
      .finally(() => setLoadingSchedule(false));
  }, [eventId]); // eslint-disable-line

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!currentDay) return;
    setDayTitle(currentDay.title);
    setEventDate(currentDay.date);
    setStartTime(currentDay.startTime);
    setEndTime(currentDay.endTime);
  }, [currentDayIndex, days.length]); // eslint-disable-line

  useEffect(() => {
    if (previewDayIndex >= days.length && days.length > 0) {
      setPreviewDayIndex(days.length - 1);
    }
  }, [days.length]); // eslint-disable-line

  // ─── Day helpers ─────────────────────────────────────────────────────────
  const updateDay = (idx, patch) =>
    setDays((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

  const updateSchedule = (idx, schedule) => updateDay(idx, { schedule });

  const saveDayFields = (overrides = {}) => {
    const merged = {
      title: overrides.title ?? dayTitle,
      date: overrides.date ?? eventDate,
      startTime: overrides.startTime ?? startTime,
      endTime: overrides.endTime ?? endTime,
    };
    setDays((prev) => {
      const updated = prev.map((d, i) =>
        i === currentDayIndex ? { ...d, ...merged } : d,
      );
      const sorted = [...updated].sort((a, b) =>
        a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
      );
      const updatedDayId = updated[currentDayIndex].id;
      const newIdx = sorted.findIndex((d) => d.id === updatedDayId);
      const renamed = sorted.map((d, i) => ({ ...d, title: `Day ${i + 1}` }));
      setTimeout(() => setCurrentDayIndex(newIdx >= 0 ? newIdx : 0), 0);
      return renamed;
    });
  };

  const handleDateChange = (val) => {
    setEventDate(val);
    saveDayFields({ date: val });
  };

  const handleStartTimeChange = (val) => {
    setStartTime(val);
    saveDayFields({ startTime: val });
  };

  const handleEndTimeChange = (val) => {
    setEndTime(val);
    saveDayFields({ endTime: val });
  };

  const createDay = () => {
    if (!currentDay || currentDay.schedule.length === 0) {
      alert(
        "Please add at least one activity to the current day's schedule before adding a new day."
      );
      return;
    }

    const nextDate = addCalendarDays(currentDay.date, 1);

    // Prevent creating days beyond the event's end date.
    if (eventEndDate && nextDate > eventEndDate) {
      alert("You have reached the event's end date.");
      return;
    }

    const newDay = {
      id: uniqueId(),
      title: `Day ${days.length + 1}`,
      date: nextDate,
      startTime: currentDay.startTime,
      endTime: currentDay.endTime,
      schedule: [],
    };

    setDays((previousDays) => [...previousDays, newDay]);
    setCurrentDayIndex(days.length);
    setDayTitle(newDay.title);
    setEventDate(newDay.date);
    setStartTime(newDay.startTime);
    setEndTime(newDay.endTime);
  };

  const removeDay = (idx, e) => {
    e.stopPropagation();
    if (days.length === 1) {
      alert("You must have at least one day.");
      return;
    }
    if (
      !window.confirm(
        `Remove Day ${idx + 1}? All its scheduled activities will be lost.`,
      )
    )
      return;
    setDays((prev) => {
      const filtered = prev.filter((_, i) => i !== idx);
      const renamed = filtered.map((d, i) => ({ ...d, title: `Day ${i + 1}` }));
      return renamed;
    });
    setCurrentDayIndex((prev) => Math.min(prev, days.length - 2));
  };

  // ─── Activity helpers ─────────────────────────────────────────────────────
  const addCustomActivity = () => {
    const name = newActName.trim();
    if (!name) return alert("Please enter an activity name.");
    if (newActDur < 5 || newActDur > 240)
      return alert("Duration must be between 5 and 240 minutes.");
    const maxId = activityTemplates.reduce((m, a) => Math.max(m, a.id), 0);
    setActivityTemplates((prev) => [
      ...prev,
      {
        id: maxId + 1,
        name,
        duration: newActDur,
        type: "break",
        isCustom: true,
      },
    ]);
    setNewActName("");
    setNewActDur(30);
    notify(`"${name}" added to activities`);
  };

  // Adds the activity at the first real gap that fits, starting exactly
  // where the previous activity ends (no fixed 30-min grid).
  const addToNextSlot = (activity) => {
    if (!currentDay) return;
    const start = timeToMinutes(currentDay.startTime);
    const end = timeToMinutes(currentDay.endTime);
    const sorted = [...currentDay.schedule].sort(
      (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
    );
    let cur = start;
    let slotFound = false;
    for (let i = 0; i <= sorted.length; i++) {
      const next =
        i === sorted.length ? end : timeToMinutes(sorted[i].startTime);
      if (next - cur >= activity.duration) {
        slotFound = true;
        break;
      }
      if (i < sorted.length) {
        cur = Math.max(
          cur,
          timeToMinutes(sorted[i].startTime) + sorted[i].duration,
        );
      }
    }
    if (!slotFound) {
      alert("No available time slot. Remove activities or extend event hours.");
      return;
    }
    const newSched = [
      ...currentDay.schedule,
      {
        id: uniqueId(),
        templateId: activity.id,
        name: activity.name,
        duration: activity.duration,
        startTime: minutesToTime(cur),
        type: activity.type || "break",
        speakerId: activity.speakerId ?? null,
        isCustom: activity.isCustom || false,
        room: null,
      },
    ].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
    updateSchedule(currentDayIndex, newSched);
    notify(`"${activity.name}" added at ${formatTime12Hour(minutesToTime(cur))}`);
  };

  const deleteActivityFromSchedule = (id) => {
    updateSchedule(
      currentDayIndex,
      currentDay.schedule.filter((a) => a.id !== id),
    );
    notify("Activity removed");
  };

  const clearDay = () => {
    if (!window.confirm("Clear all activities from this day's schedule?"))
      return;
    updateSchedule(currentDayIndex, []);
    notify("Schedule cleared");
  };

  // ─── Room helpers ────────────────────────────────────────────────────────
  const addRoom = () => {
    const name = newRoomName.trim();
    if (!name) return alert("Please enter a room name.");
    if (rooms.some((r) => r.name.toLowerCase() === name.toLowerCase()))
      return alert("This room already exists.");
    setRooms((prev) => [...prev, { id: uniqueId(), name }]);
    setNewRoomName("");
    notify(`Room "${name}" added`);
  };

  const removeRoom = (room) => {
    if (
      !window.confirm(
        `Remove room "${room.name}"? It will also be cleared from any slots using it.`,
      )
    )
      return;
    setRooms((prev) => prev.filter((r) => r.id !== room.id));
    setDays((prev) =>
      prev.map((d) => ({
        ...d,
        schedule: d.schedule.map((a) =>
          a.room === room.name ? { ...a, room: null } : a,
        ),
      })),
    );
    notify(`Room "${room.name}" removed`);
  };

  const setActivityRoom = (actId, roomName) => {
    if (!currentDay) return;
    updateSchedule(
      currentDayIndex,
      currentDay.schedule.map((a) =>
        a.id === actId ? { ...a, room: roomName || null } : a,
      ),
    );
    const act = currentDay.schedule.find((a) => a.id === actId);
    notify(
      roomName
        ? `"${act?.name}" set to ${roomName}`
        : `Room cleared for "${act?.name}"`,
    );
  };

  const openRoomModal = (actId) => {
    setRoomTargetActId(actId);
    setRoomModalOpen(true);
  };

  // ─── Drag and drop ───────────────────────────────────────────────────────
  const handleDragStart = (item) => {
    dragRef.current = item;
  };

  // true if no two items overlap and all are inside the day window
  const isScheduleValid = (list, day) => {
    const dayStart = timeToMinutes(day.startTime);
    const dayEnd = timeToMinutes(day.endTime);
    const sorted = [...list].sort(
      (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
    );
    for (let i = 0; i < sorted.length; i++) {
      const s = timeToMinutes(sorted[i].startTime);
      const e = s + sorted[i].duration;
      if (s < dayStart || e > dayEnd) return false;
      if (i > 0) {
        const prev = sorted[i - 1];
        if (timeToMinutes(prev.startTime) + prev.duration > s) return false;
      }
    }
    return true;
  };

  // Drop on an EMPTY slot (new item from palette OR move an existing card)
  const handleDrop = (slotTime) => {
    const item = dragRef.current;
    dragRef.current = null;
    setDragOverActId(null);
    if (!item || !currentDay) return;

    if (item.source === "room") {
      alert("Drop a room onto a scheduled card to assign it.");
      return;
    }

    // ── Move an already-scheduled card ──
    if (item.source === "schedule") {
      const act = currentDay.schedule.find((a) => a.id === item.actId);
      if (!act) return;
      const others = currentDay.schedule.filter((a) => a.id !== act.id);
      const moved = { ...act, startTime: slotTime };
      if (!isScheduleValid([...others, moved], currentDay)) {
        alert(
          "Cannot move here: it would overlap another activity or exceed the day's time.",
        );
        return;
      }
      updateSchedule(
        currentDayIndex,
        [...others, moved].sort(
          (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
        ),
      );
      notify(`"${act.name}" moved to ${formatTime12Hour(slotTime)}`);
      return;
    }

    // ── New item from the palette ──
    let newItem;
    if (item.type === "speaker") {
      const sp = speakers.find((s) => s.id === item.id);
      newItem = {
        id: uniqueId(),
        templateId: null,
        name: sp?.name || "Speaker",
        duration: item.duration,
        startTime: slotTime,
        type: "speaker",
        speakerId: sp?.id ?? null,
        isCustom: false,
        room: null,
      };
    } else {
      newItem = {
        id: uniqueId(),
        templateId: item.id,
        name: item.name,
        duration: item.duration,
        startTime: slotTime,
        type: item.type || "break",
        speakerId: null,
        isCustom: item.isCustom || false,
        room: null,
      };
    }

    if (!isScheduleValid([...currentDay.schedule, newItem], currentDay)) {
      alert(
        "Cannot add here: it would overlap another activity or exceed the day's end time.",
      );
      return;
    }

    updateSchedule(
      currentDayIndex,
      [...currentDay.schedule, newItem].sort(
        (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
      ),
    );
    notify(
      item.type === "speaker"
        ? `Speaker scheduled at ${formatTime12Hour(slotTime)}`
        : `"${item.name}" added at ${formatTime12Hour(slotTime)}`,
    );
  };

  // Drop on an OCCUPIED card → assign a ROOM, or SWAP positions
  const handleDropOnActivity = (targetId) => {
    const item = dragRef.current;
    dragRef.current = null;
    setDragOverActId(null);
    if (!item || !currentDay) return;

    // Room chip dropped on a card → assign room
    if (item.source === "room") {
      setActivityRoom(targetId, item.name);
      return;
    }

    if (item.source !== "schedule") {
      alert(
        "Drop on an empty slot, or drag a scheduled card onto another card to swap.",
      );
      return;
    }
    if (item.actId === targetId) return;

    const a = currentDay.schedule.find((x) => x.id === item.actId);
    const b = currentDay.schedule.find((x) => x.id === targetId);
    if (!a || !b) return;

    const [first, second] =
      timeToMinutes(a.startTime) <= timeToMinutes(b.startTime) ? [a, b] : [b, a];

    const spanStart = timeToMinutes(first.startTime);
    const spanEnd = timeToMinutes(second.startTime) + second.duration;

    // later item takes the earlier start; earlier item ends where the later one ended
    const newSecondStart = spanStart;
    const newFirstStart = spanEnd - first.duration;

    const updated = currentDay.schedule
      .map((x) => {
        if (x.id === second.id)
          return { ...x, startTime: minutesToTime(newSecondStart) };
        if (x.id === first.id)
          return { ...x, startTime: minutesToTime(newFirstStart) };
        return x;
      })
      .sort((p, q) => timeToMinutes(p.startTime) - timeToMinutes(q.startTime));

    if (!isScheduleValid(updated, currentDay)) {
      alert("Cannot swap: it would cause an overlap with another activity.");
      return;
    }

    updateSchedule(currentDayIndex, updated);
    notify(`Swapped "${first.name}" and "${second.name}"`);
  };

  // ─── Duration editor ─────────────────────────────────────────────────────
  const openDurEditor = (actId, dayIdx) => {
    const act = days[dayIdx]?.schedule.find((a) => a.id === actId);
    if (!act) return;
    setDurEditorActId(actId);
    setDurEditorDayIdx(dayIdx);
    setDurValue(act.duration);
    setDurEditorOpen(true);
  };

  // Changing a duration automatically re-flows the activities after it:
  //  • longer  → following activities are pushed later (only as far as needed)
  //  • shorter → activities that were back-to-back move up to close the gap
  //  • existing gaps between activities are preserved
  const applyDuration = () => {
    if (durValue < 5 || durValue > 240)
      return alert("Duration must be 5–240 min");
    const day = days[durEditorDayIdx];
    const act = day?.schedule.find((a) => a.id === durEditorActId);
    if (!act) return;

    const dayEnd = timeToMinutes(day.endTime);
    const actStart = timeToMinutes(act.startTime);
    if (actStart + durValue > dayEnd) return alert("Exceeds event end time.");

    const sorted = [...day.schedule].sort(
      (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
    );
    const idx = sorted.findIndex((a) => a.id === act.id);

    const changes = new Map(); // id -> { startTime, duration }
    changes.set(act.id, { startTime: act.startTime, duration: durValue });

    let prevOldEnd = actStart + act.duration;
    let prevNewEnd = actStart + durValue;

    for (let i = idx + 1; i < sorted.length; i++) {
      const a = sorted[i];
      const oldStart = timeToMinutes(a.startTime);
      const oldEnd = oldStart + a.duration;
      // back-to-back with the previous one → stay glued to it; otherwise only
      // move if the previous one now runs into it
      const newStart =
        oldStart === prevOldEnd
          ? prevNewEnd
          : Math.max(oldStart, prevNewEnd);

      if (newStart + a.duration > dayEnd) {
        return alert(
          `Cannot change duration: "${a.name}" would run past the event end time. Extend the day's end time or remove an activity first.`,
        );
      }

      changes.set(a.id, {
        startTime: minutesToTime(newStart),
        duration: a.duration,
      });
      prevOldEnd = oldEnd;
      prevNewEnd = newStart + a.duration;
    }

    const newSched = day.schedule
      .map((a) => (changes.has(a.id) ? { ...a, ...changes.get(a.id) } : a))
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

    updateSchedule(durEditorDayIdx, newSched);
    setDurEditorOpen(false);
    notify("Duration updated — following slots adjusted");
  };

  // ─── Speaker assignment ──────────────────────────────────────────────────
  const openSpeakerModal = (actId) => {
    setSelectedActId(actId);
    setSelectedSpeakerId(null);
    setSpeakerModalOpen(true);
  };

  const assignSpeaker = () => {
    if (!selectedSpeakerId) return alert("Please select a speaker.");
    const sp = speakers.find((s) => s.id === selectedSpeakerId);
    updateSchedule(
      currentDayIndex,
      currentDay.schedule.map((a) => {
        if (a.id !== selectedActId) return a;
        return { ...a, speakerId: sp.id, name: sp.name };
      }),
    );
    notify(`Assigned ${sp.name}`);
    setSpeakerModalOpen(false);
  };

  // ─── Schedule slot generation ─────────────────────────────────────────────
  // Builds slots from the real gaps between activities so every available
  // slot starts exactly where the previous activity ended.
  const generateSlots = () => {
    if (!currentDay) return [];
    const dayStart = timeToMinutes(currentDay.startTime);
    const dayEnd = timeToMinutes(currentDay.endTime);
    const sorted = [...currentDay.schedule].sort(
      (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
    );
    const slots = [];

    const pushGap = (gapStart, gapEnd) => {
      for (let t = gapStart; t < gapEnd; t += 30) {
        slots.push({
          type: "empty",
          time: minutesToTime(t),
          endTime: minutesToTime(Math.min(t + 30, gapEnd)),
        });
      }
    };

    let cursor = dayStart;
    sorted.forEach((act) => {
      const s = timeToMinutes(act.startTime);
      if (s > cursor) pushGap(cursor, s);
      slots.push({ type: "occupied", time: act.startTime, activity: act });
      cursor = Math.max(cursor, s + act.duration);
    });
    if (cursor < dayEnd) pushGap(cursor, dayEnd);

    return slots;
  };

  const slots = generateSlots();

  const hasAtLeastOneTask = days.some((d) => d.schedule.length > 0);

  // ─── Export to PDF ────────────────────────────────────────────────────────
  // Opens the browser's print dialog with a clean schedule document;
  // choose "Save as PDF" as the destination.
  const exportSchedulePDF = () => {
    const nonEmptyDays = days.filter((d) => d.schedule.length > 0);
    if (nonEmptyDays.length === 0) {
      alert("Add at least one activity before exporting.");
      return;
    }

  const html = buildScheduleHtml(
  nonEmptyDays,
  speakers,
  eventName,
  eventLogo,
  eventStartDate,
  eventEndDate
);

    const iframe = document.createElement("iframe");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    iframe.contentWindow.onafterprint = cleanup;
   setTimeout(async () => {
  try {
    const printWindow = iframe.contentWindow;
    const printDocument = printWindow.document;

    // Wait for the logo and other images to load.
    await Promise.all(
      Array.from(printDocument.images).map((img) => {
        if (img.complete) return Promise.resolve();

        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;

          // Do not block printing indefinitely.
          setTimeout(resolve, 4000);
        });
      })
    );

    printWindow.focus();
    printWindow.print();
  } catch (err) {
    console.error("Print failed:", err);
    cleanup();
  }
}, 350);
    setTimeout(cleanup, 120000);
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!hasAtLeastOneTask) return;

    const activitiesPayload = [];
    days.forEach((day) => {
      day.schedule.forEach((task) => {
        if (
          task.type !== "speaker" &&
          !activitiesPayload.find((a) => a.name === task.name)
        ) {
          activitiesPayload.push({
            name: task.name,
            time: minutesToTime(task.duration),
          });
        }
      });
    });

    const daysPayload = days
      .filter((day) => day.schedule.length > 0)
      .map((day) => ({
        start_date: day.date,
        start_time: day.startTime,
        end_time: day.endTime,
        scheduled_task: [...day.schedule]
          .sort(
            (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
          )
          .map((task) => ({
            start_time: task.startTime,
            end_time: minutesToTime(
              timeToMinutes(task.startTime) + task.duration,
            ),
            room: task.room || null,
            activity:
              task.type === "speaker"
                ? {
                  type: "speaker",
                  name: task.name,
                  speaker_id: String(task.speakerId),
                }
                : {
                  type: "break",
                  name: task.name,
                },
          })),
      }));

    const payload = {
      eventId: Number(eventId),
      scheduleData: {
        activities: activitiesPayload,
        rooms: rooms.map((r) => ({ name: r.name })),
        day: daysPayload,
      },
    };

    const token = localStorage.getItem("token");
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/event_scheduler`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(
          errData?.message || `Request failed with status ${response.status}`,
        );
      }

      navigate("/dashboard", {
        state: { message: "Create event scheduler successfully!" },
      });
    } catch (err) {
      console.error("Submit error:", err);
      alert(`Failed to submit scheduler: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const isPageLoading = loadingActivities || loadingSpeakers || loadingSchedule;

  // ─── Render ───────────────────────────────────────────────────────────────
  if (isPageLoading) {
    return (
      <div
        style={{
          background: "#f4f5fb",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Segoe UI', system-ui, sans-serif",
          flexDirection: "column",
          gap: 16,
          color: "#475569",
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            border: "4px solid #e2e8f0",
            borderTop: "4px solid #2D2856",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ margin: 0, fontSize: "1rem", fontWeight: 500 }}>
          Loading scheduler…
        </p>
      </div>
    );
  }

  return (
    <DashboardLayout currentSection="speaking">
      <div
        className="scheduler-page"
        style={{
          background: "#f7f7f5",
          minHeight: "100vh",
          fontFamily: "'Segoe UI', system-ui, sans-serif",
          color: "#334155",
        }}
      >
        <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        .scheduler-page,
  .scheduler-page * {
    box-sizing: border-box;
  }

  .scheduler-page input,
  .scheduler-page select {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    font-size: 0.95rem;
    background: #f8fafc;
    transition: all 0.2s;
    outline: none;
    font-family: inherit;
    color: #334155;
  }

  .scheduler-page input:focus,
  .scheduler-page select:focus {
    border-color: #2D2856;
    background: white;
    box-shadow: 0 0 0 3px rgba(45, 40, 86, 0.1);
  }

  .scheduler-page label {
    display: block;
    margin-bottom: 6px;
    font-weight: 600;
    color: #475569;
    font-size: 0.87rem;
  }

  .scheduler-page button {
    font-family: inherit;
  }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 3px; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

        /* ── Responsive layout ── */
        .scheduler-main-grid {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 22px;
          margin-bottom: 22px;
        }
        .day-fields-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(140px, 1fr));
          gap: 16px;
        }
        .custom-act-grid {
          display: grid;
          grid-template-columns: 1fr 70px auto;
          gap: 7px;
          align-items: end;
        }
        .room-add-grid {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 7px;
          align-items: end;
        }
        .header-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .scheduler-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }

        /* ── Compact preview rows ── */
        .pv-row {
          display: grid;
          grid-template-columns: 112px 1fr auto;
          gap: 45px;
          align-items: center;
          padding: 8px 12px;
          background: white;
          border: 1px solid #eef0f4;
          border-left: 3px solid #2D2856;
          border-radius: 5px;
          margin-bottom: 6px;
        }
        .pv-row:last-child { margin-bottom: 0; }
        .pv-row:hover { background: #fafafd; }

        @media (max-width: 900px) {
          .scheduler-main-grid {
            grid-template-columns: 1fr;
          }
          .day-fields-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 600px) {
          .day-fields-grid {
            grid-template-columns: 1fr;
          }
          .custom-act-grid {
            grid-template-columns: 1fr 60px auto;
            gap: 6px;
          }
          .scheduler-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .header-actions {
            width: 100%;
            justify-content: flex-end;
          }
          .pv-row {
            grid-template-columns: 96px 1fr;
            gap: 8px;
          }
          .pv-room { grid-column: 2; justify-self: start; }
          .pv-room:empty { display: none; }
        }

        
      `}</style>

        {isPageLoading && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(244,245,251,0.88)",
              zIndex: 2000,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                border: "4px solid #e2e8f0",
                borderTop: "4px solid #2D2856",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <p style={{ color: "#475569", fontWeight: 600, margin: 0 }}>
              Loading scheduler…
            </p>
          </div>
        )}

        <div style={{ maxWidth: 1700, margin: "0 auto", padding: "16px 14px" }}>

          {/* ══ HEADER ══ */}
         {/* ══ HEADER ══ */}
<div
  className="scheduler-header"
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 24,
    background: "#2D2856",
    color: "white",
    padding: "22px 24px",
    borderRadius: 6,
    marginBottom: 18,
    boxShadow: "0 10px 30px rgba(45,40,86,.22)",
  }}
>
  {/* Left: Scheduler title + event information */}
  <div style={{ minWidth: 0, flex: "1 1 auto" }}>
    <h1
      style={{
        fontSize: "clamp(1.4rem, 3vw, 1.9rem)",
        fontWeight: 800,
        margin: 0,
        lineHeight: 1.25,
        color: "#ffffff",
      }}
    >
      Event Scheduler
    </h1>

    <p
      style={{
        color: "rgba(255,255,255,0.78)",
        fontSize: "0.92rem",
        margin: "6px 0 0",
        lineHeight: 1.5,
      }}
    >
      Build your event agenda — add days, schedule activities &amp; assign
      speakers.
    </p>

    {/* Event details */}
    <div
      className="scheduler-event-meta"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px 20px",
        marginTop: 14,
      }}
    >
      {/* Event logo + name */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minWidth: 0,
        }}
      >
        {eventLogo ? (
          <img
            src={eventLogo}
            alt={`${eventName || "Event"} logo`}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            style={{
              width: 38,
              height: 38,
              objectFit: "contain",
              borderRadius: 7,
              background: "#ffffff",
              padding: 3,
              flexShrink: 0,
            }}
          />

          
        ) : (
          <div
            aria-hidden="true"
            style={{
              width: 38,
              height: 38,
              borderRadius: 7,
              border: "1px solid rgba(255,255,255,.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {(eventName || "E").charAt(0).toUpperCase()}
          </div>
        )}

        <span
          style={{
            fontSize: "clamp(0.95rem, 2vw, 1.2rem)",
            fontWeight: 700,
            color: "#ffffff",
            overflowWrap: "anywhere",
          }}
        >
          {eventName || "Event details loading..."}
        </span>
      </div>

      {/* Vertical divider */}
      <div
        className="scheduler-event-divider"
        style={{
          width: 1,
          height: 30,
          background: "rgba(255,255,255,.35)",
        }}
      />

      {/* Event date range */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          flexWrap: "wrap",
          color: "#D7D0FF",
          fontSize: "0.94rem",
          fontWeight: 500,
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 19 }}>
          ▦
        </span>

        <span>
          {eventStartDate
            ? new Date(
                `${eventStartDate}T00:00:00`
              ).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "Start date unavailable"}

          {" – "}

          {eventEndDate
            ? new Date(
                `${eventEndDate}T00:00:00`
              ).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : eventStartDate
              ? new Date(
                  `${eventStartDate}T00:00:00`
                ).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "End date unavailable"}
        </span>
      </div>
    </div>
  </div>

  {/* Right: Existing action buttons */}
  <div
    className="header-actions"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 12,
      flexShrink: 0,
      flexWrap: "wrap",
    }}
  >
    <Btn
      onClick={() => setPreviewModalOpen(true)}
      variant="outline"
      style={{
        color: "white",
        borderColor: "white",
      }}
    >
      Full Preview
    </Btn>

    <Btn
      onClick={exportSchedulePDF}
      variant="outline"
      disabled={!hasAtLeastOneTask}
      style={{
        color: "white",
        borderColor: "white",
      }}
    >
      Export PDF
    </Btn>

    <Btn
      onClick={handleSubmit}
      disabled={!hasAtLeastOneTask || submitting}
      style={{
        background: "#10b981",
      }}
    >
      {submitting ? "Submitting..." : "Submit"}
    </Btn>
  </div>
</div>

          {/* ══ DAY SETUP PANEL ══ */}
          <div
            style={{
              background: "white",
              borderRadius: 5,
              padding: "18px 16px",
              marginBottom: 18,
              boxShadow: "0 4px 20px rgba(0,0,0,.06)",
            }}
          >
            {/* Day tabs */}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 18,
                paddingBottom: 14,
                borderBottom: "2px solid #f1f5f9",
                overflowX: "auto",
              }}
            >
              {days.map((d, i) => (
                <div
                  key={d.id}
                  onClick={() => setCurrentDayIndex(i)}
                  style={{
                    padding: "8px 14px 8px 18px",
                    borderRadius: 50,
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    background: i === currentDayIndex ? "#2D2856" : "#f1f5f9",
                    color: i === currentDayIndex ? "white" : "#64748b",
                    boxShadow:
                      i === currentDayIndex
                        ? "0 4px 12px rgba(45,40,86,.3)"
                        : "none",
                    transition: "all .2s",
                    userSelect: "none",
                    flexShrink: 0,
                  }}
                >
                  {d.title}
                  <span
                    onClick={(e) => removeDay(i, e)}
                    title={`Remove ${d.title}`}
                    style={{
                      width: 17,
                      height: 17,
                      borderRadius: "50%",
                      background:
                        i === currentDayIndex
                          ? "rgba(255,255,255,0.25)"
                          : "#cbd5e1",
                      color: i === currentDayIndex ? "white" : "#64748b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      flexShrink: 0,
                      lineHeight: 1,
                    }}
                  >
                    ✕
                  </span>
                </div>
              ))}
              <div
                onClick={createDay}
                title={
                  currentDay && currentDay.schedule.length === 0
                    ? "Add at least one activity to current day first"
                    : "Add new day"
                }
                style={{
                  padding: "8px 16px",
                  borderRadius: 50,
                  cursor:
                    currentDay && currentDay.schedule.length === 0
                      ? "not-allowed"
                      : "pointer",
                  border: "2px dashed #94a3b8",
                  color:
                    currentDay && currentDay.schedule.length === 0
                      ? "#cbd5e1"
                      : "#64748b",
                  background: "transparent",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all .2s",
                  opacity:
                    currentDay && currentDay.schedule.length === 0 ? 0.5 : 1,
                  flexShrink: 0,
                }}
              >
                + Add Day
              </div>
            </div>

            {/* Day form fields */}
            <div className="day-fields-grid">
              <div>
                <label>Day Title</label>
                <input
                  value={dayTitle}
                  onChange={(e) => setDayTitle(e.target.value)}
                  onBlur={() => saveDayFields({ title: dayTitle })}
                  placeholder="e.g. Day 1"
                />
              </div>
              <div>
                <label>Date</label>
                <input
                  type="date"
                  value={eventDate}
                  min={todayISO()}
                  onChange={(e) => handleDateChange(e.target.value)}
                />
              </div>
              <div>
                <label>Start Time</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                />
              </div>
              <div>
                <label>End Time</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => handleEndTimeChange(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ══ MAIN GRID ══ */}
          <div className="scheduler-main-grid">

            {/* ── Left Column ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

              {/* Activities panel */}
              <div
                style={{
                  background: "white",
                  borderRadius: 5,
                  padding: "18px 16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,.06)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "2px solid #f1f5f9",
                    paddingBottom: 12,
                    marginBottom: 14,
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <h2 style={{ color: "#475569", fontSize: "1rem", margin: 0 }}>
                    Activities
                  </h2>
                  <Btn onClick={clearDay} style={{ background: "#db540cff" }} variant="secondary" size="sm" >
                    Clear Schedule
                  </Btn>
                </div>
                <p
                  style={{
                    color: "#76a2afff",
                    fontSize: "0.84rem",
                    marginBottom: 12,
                  }}
                >
                  Drag or click to add to next available slot.
                </p>

                {loadingActivities ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#94a3b8",
                    }}
                  >
                    Loading activities...
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 7,
                      maxHeight: 200,
                      overflowY: "auto",
                      padding: 9,
                      border: "1px solid #f1f5f9",
                      borderRadius: 5,
                      marginBottom: 14,
                    }}
                  >
                    {activityTemplates.map((act) => (
                      <div
                        key={act.id}
                        draggable
                        onDragStart={() => handleDragStart({ ...act })}
                        onClick={() => addToNextSlot(act)}
                        style={{
                          padding: "6px 12px",
                          background: act.isCustom ? "#fef9ec" : "#f1f5f9",
                          borderRadius: 50,
                          cursor: "grab",
                          fontWeight: 500,
                          color: "#475569",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: "0.82rem",
                          border: `1px solid ${act.isCustom ? "#fcd34d" : "#e2e8f0"}`,
                          transition: "all .2s",
                          userSelect: "none",
                        }}
                      >
                        {act.name}
                        <span
                          style={{
                            background: "#2D2856",
                            color: "white",
                            fontSize: "0.67rem",
                            padding: "2px 6px",
                            borderRadius: 5,
                          }}
                        >
                          {act.duration}m
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add custom activity */}
                <div className="custom-act-grid">
                  <div>
                    <label>Name</label>
                    <input
                      value={newActName}
                      onChange={(e) => setNewActName(e.target.value)}
                      placeholder="Activity name"
                      onKeyDown={(e) => e.key === "Enter" && addCustomActivity()}
                      style={{ padding: "10px" }}
                    />
                  </div>
                  <div>
                    <label>Min</label>
                    <input
                      type="number"
                      min={5}
                      max={240}
                      step={5}
                      value={newActDur}
                      onChange={(e) => setNewActDur(+e.target.value)}
                    />
                  </div>
                  <Btn
                    onClick={addCustomActivity}
                    size="sm"
                    style={{ alignSelf: "end", height: 44 }}
                  >
                    Add
                  </Btn>
                </div>
              </div>

              {/* Speakers panel */}
              <div
                style={{
                  background: "white",
                  borderRadius: 5,
                  padding: "18px 16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,.06)",
                }}
              >
                <div
                  style={{
                    borderBottom: "2px solid #f1f5f9",
                    paddingBottom: 12,
                    marginBottom: 14,
                  }}
                >
                  <h2 style={{ color: "#475569", fontSize: "1rem", margin: 0 }}>
                    Speakers
                  </h2>
                </div>
                <p
                  style={{
                    color: "#64748b",
                    fontSize: "0.84rem",
                    marginBottom: 12,
                  }}
                >
                  Drag to schedule a speaker session.
                </p>

                {loadingSpeakers ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#94a3b8",
                    }}
                  >
                    Loading speakers...
                  </div>
                ) : speakers.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#94a3b8",
                      fontSize: "0.88rem",
                    }}
                  >
                    No approved speakers yet.
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      maxHeight: 360,
                      overflowY: "auto",
                      padding: 8,
                      border: "1px solid #f1f5f9",
                      borderRadius: 5,
                    }}
                  >
                    {speakers.map((sp) => (
                      <div
                        key={sp.id}
                        draggable
                        onDragStart={() =>
                          handleDragStart({
                            id: sp.id,
                            name: sp.name,
                            duration: 30,
                            type: "speaker",
                          })
                        }
                        style={{
                          background: "#f8fafc",
                          borderRadius: 5,
                          padding: 10,
                          border: "1px solid #e2e8f0",
                          cursor: "grab",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          transition: "all .2s",
                          userSelect: "none",
                        }}
                      >
                        <Avatar
                          name={sp.name}
                          photoBase64={sp.profile_picture?.base64}
                          size={36}
                        />
                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              color: "#334155",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.88rem",
                            }}
                          >
                            {sp.name}
                          </div>
                          <div
                            style={{
                              color: "#64748b",
                              fontSize: "0.76rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sp.jobtitle}
                            {sp.company ? ` · ${sp.company}` : ""}
                          </div>
                          {getTalkTopic(sp) && (
                            <div
                              title={getTalkTopic(sp)}
                              style={{
                                color: "#2D2856",
                                fontSize: "0.76rem",
                                fontWeight: 600,
                                marginTop: 2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              💬 {getTalkTopic(sp)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rooms panel */}
              <div
                style={{
                  background: "white",
                  borderRadius: 5,
                  padding: "18px 16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,.06)",
                }}
              >
                <div
                  style={{
                    borderBottom: "2px solid #f1f5f9",
                    paddingBottom: 12,
                    marginBottom: 14,
                  }}
                >
                  <h2 style={{ color: "#475569", fontSize: "1rem", margin: 0 }}>
                    Rooms
                  </h2>
                </div>
                <p
                  style={{
                    color: "#64748b",
                    fontSize: "0.84rem",
                    marginBottom: 12,
                  }}
                >
                  Add rooms, then drag one onto a scheduled card (or click 📍 on
                  the card) to set its location.
                </p>

                {rooms.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "14px",
                      color: "#94a3b8",
                      fontSize: "0.86rem",
                      border: "1px solid #f1f5f9",
                      borderRadius: 5,
                      marginBottom: 14,
                    }}
                  >
                    No rooms yet. Add your first room below.
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 7,
                      maxHeight: 160,
                      overflowY: "auto",
                      padding: 9,
                      border: "1px solid #f1f5f9",
                      borderRadius: 5,
                      marginBottom: 14,
                    }}
                  >
                    {rooms.map((room) => (
                      <div
                        key={room.id}
                        draggable
                        onDragStart={() =>
                          handleDragStart({ source: "room", name: room.name })
                        }
                        style={{
                          padding: "6px 8px 6px 12px",
                          background: "#ecfdf5",
                          borderRadius: 50,
                          cursor: "grab",
                          fontWeight: 500,
                          color: "#065f46",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: "0.82rem",
                          border: "1px solid #a7f3d0",
                          transition: "all .2s",
                          userSelect: "none",
                        }}
                      >
                        📍 {room.name}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            removeRoom(room);
                          }}
                          title="Remove room"
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            background: "#a7f3d0",
                            color: "#065f46",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            cursor: "pointer",
                            lineHeight: 1,
                          }}
                        >
                          ✕
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="room-add-grid">
                  <div>
                    <label>Room name</label>
                    <input
                      value={newRoomName}
                      onChange={(e) => setNewRoomName(e.target.value)}
                      placeholder="e.g. Main Hall"
                      onKeyDown={(e) => e.key === "Enter" && addRoom()}
                      style={{ padding: "10px" }}
                    />
                  </div>
                  <Btn
                    onClick={addRoom}
                    size="sm"
                    style={{ alignSelf: "end", height: 44 }}
                  >
                    Add
                  </Btn>
                </div>
              </div>
            </div>

            {/* ── Schedule Builder ── */}
            <div
              style={{
                background: "white",
                borderRadius: 5,
                padding: "18px 16px",
                boxShadow: "0 4px 20px rgba(0,0,0,.06)",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "2px solid #f1f5f9",
                  paddingBottom: 12,
                  marginBottom: 14,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <h2 style={{ color: "#475569", fontSize: "1rem", margin: 0 }}>
                  Builder —{" "}
                  <span style={{ color: "#2D2856" }}>
                    {currentDay?.title || "No Day Selected"}
                  </span>
                </h2>
                <Btn onClick={clearDay} style={{ background: "#db540cff" }} variant="secondary" size="sm">
                  Clear Day
                </Btn>
              </div>
              <p
                style={{
                  color: "#64748b",
                  fontSize: "0.84rem",
                  marginBottom: 14,
                }}
              >
                Drag activities or speakers onto a slot. Drag a scheduled card onto another card to swap. Click ⏱ to edit duration, 📍 to set a room.
              </p>

              <div
                style={{
                  minHeight: 400,
                  border: "2px dashed #e2e8f0",
                  borderRadius: 5,
                  padding: 12,
                  background: "#f8fafc",
                  overflowY: "auto",
                  maxHeight: "84vh",
                }}
              >
                {!currentDay ? (
                  <div
                    style={{ textAlign: "center", padding: "50px 20px", color: "#94a3b8" }}
                  >
                    <div style={{ fontSize: "3rem", marginBottom: 10 }}>📅</div>
                    <h3 style={{ marginBottom: 6 }}>No Day Selected</h3>
                    <p>Create or select a day to begin.</p>
                  </div>
                ) : slots.length === 0 ? (
                  <div
                    style={{ textAlign: "center", padding: "50px 20px", color: "#94a3b8" }}
                  >
                    No time slots available for this day.
                  </div>
                ) : (
                  slots.map((slot, i) => {
                    if (slot.type === "occupied") {
                      const act = slot.activity;
                      const sp = act.speakerId
                        ? speakers.find((s) => s.id === act.speakerId)
                        : null;
                      const borderColor =
                        act.type === "speaker" ? "#2D2856" : "#f97316";
                      return (
                        <div
                          key={`${act.id}-${i}`}
                          draggable
                          onDragStart={() =>
                            handleDragStart({ source: "schedule", actId: act.id })
                          }
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOverActId(act.id);
                          }}
                          onDragLeave={() => setDragOverActId(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            handleDropOnActivity(act.id);
                          }}
                          onDragEnd={() => setDragOverActId(null)}
                          style={{
                            background:
                              dragOverActId === act.id
                                ? "rgba(45,40,86,.06)"
                                : "white",
                            border: "1px solid #e2e8f0",
                            borderLeft: `4px solid ${borderColor}`,
                            borderRadius: 5,
                            padding: "11px 12px",
                            marginBottom: 10,
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            boxShadow: "0 2px 8px rgba(0,0,0,.04)",
                            minHeight: 64,
                            cursor: "grab",
                          }}
                        >
                          <div
                            style={{
                              minWidth: 130,
                              fontWeight: 700,
                              color: "#475569",
                              fontSize: "0.82rem",
                              paddingTop: 2,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatTime12Hour(act.startTime)} –{" "}
                            {formatTime12Hour(
                              minutesToTime(
                                timeToMinutes(act.startTime) + act.duration,
                              ),
                            )}
                          </div>
                          <div style={{ flexGrow: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                color: "#1a1a2e",
                                marginBottom: 6,
                                fontSize: "0.9rem",
                                wordBreak: "break-word",
                              }}
                            >
                              {act.name}
                            </div>
                            {sp && getTalkTopic(sp) && (
                              <div
                                style={{
                                  color: "#2D2856",
                                  fontSize: "0.78rem",
                                  fontWeight: 600,
                                  marginBottom: 6,
                                  wordBreak: "break-word",
                                }}
                              >
                                💬 {getTalkTopic(sp)}
                              </div>
                            )}
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                                alignItems: "center",
                              }}
                            >
                              {sp && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    background: "#f1f5f9",
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    fontSize: "0.76rem",
                                  }}
                                >
                                  <Avatar
                                    name={sp.name}
                                    photoBase64={sp.profile_picture?.base64}
                                    size={16}
                                  />
                                  {sp.name}
                                </div>
                              )}
                              <div
                                onClick={() =>
                                  openDurEditor(act.id, currentDayIndex)
                                }
                                style={{
                                  background: "#e0f2fe",
                                  color: "#0c4a6e",
                                  padding: "3px 10px",
                                  borderRadius: 20,
                                  fontSize: "0.76rem",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                ⏱ {act.duration} min
                              </div>
                              <div
                                onClick={() => openRoomModal(act.id)}
                                title="Set room"
                                style={{
                                  background: act.room ? "#ecfdf5" : "#f1f5f9",
                                  color: act.room ? "#065f46" : "#64748b",
                                  border: act.room
                                    ? "1px solid #a7f3d0"
                                    : "1px dashed #cbd5e1",
                                  padding: "3px 10px",
                                  borderRadius: 20,
                                  fontSize: "0.76rem",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                📍 {act.room || "Add room"}
                              </div>
                              {act.type === "speaker" && (
                                <Btn
                                  onClick={() => openSpeakerModal(act.id)}
                                  size="sm"
                                  style={{
                                    padding: "3px 10px",
                                    fontSize: "0.74rem",
                                  }}
                                >
                                  {act.speakerId ? "Change" : "Assign"} Speaker
                                </Btn>
                              )}
                            </div>
                          </div>
                          <div
                            onClick={() => deleteActivityFromSchedule(act.id)}
                            style={{
                              color: "#ef4444",
                              cursor: "pointer",
                              padding: "4px 5px",
                              borderRadius: 6,
                              flexShrink: 0,
                              fontSize: "1rem",
                              lineHeight: 1,
                            }}
                            title="Remove"
                          >
                            ✕
                          </div>
                        </div>
                      );
                    }
                    return (
                      <DropSlot
                        key={`empty-${slot.time}-${i}`}
                        start={slot.time}
                        end={slot.endTime}
                        onDrop={() => handleDrop(slot.time)}
                      />
                    );
                  })
                )}
              </div>

              {/* Duration editor popup */}
              {durEditorOpen && (
                <div
                  style={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                    background: "white",
                    borderRadius: 14,
                    padding: "20px 18px",
                    boxShadow: "0 12px 40px rgba(0,0,0,.18)",
                    zIndex: 600,
                    border: "1px solid #e2e8f0",
                    width: "min(320px, 90vw)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                    }}
                  >
                    <strong style={{ color: "#1a1a2e", fontSize: "1rem" }}>
                      Edit Duration
                    </strong>
                    <span
                      onClick={() => setDurEditorOpen(false)}
                      style={{
                        cursor: "pointer",
                        color: "#94a3b8",
                        fontSize: "1.4rem",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 7,
                      marginBottom: 15,
                    }}
                  >
                    {[15, 30, 45, 60, 90, 120].map((m) => (
                      <div
                        key={m}
                        onClick={() => setDurValue(m)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontSize: "0.84rem",
                          background: durValue === m ? "#2D2856" : "#f1f5f9",
                          color: durValue === m ? "white" : "#475569",
                          fontWeight: 600,
                        }}
                      >
                        {m} min
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      marginBottom: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <input
                      type="number"
                      min={5}
                      max={240}
                      step={5}
                      value={durValue}
                      onChange={(e) => setDurValue(+e.target.value)}
                      style={{ maxWidth: 90 }}
                    />
                    <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
                      minutes
                    </span>
                    <Btn onClick={applyDuration} size="sm">
                      Apply
                    </Btn>
                  </div>
                  <p style={{ fontSize: "0.79rem", color: "#64748b", margin: 0 }}>
                    Following activities adjust automatically.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ══ COMPACT LIVE PREVIEW ══ */}
          <div
            style={{
              background: "white",
              borderRadius: 5,
              padding: "14px 16px",
              marginBottom: 32,
              boxShadow: "0 4px 20px rgba(0,0,0,.06)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
                borderBottom: previewCollapsed ? "none" : "2px solid #f1f5f9",
                paddingBottom: previewCollapsed ? 0 : 12,
                marginBottom: previewCollapsed ? 0 : 12,
              }}
            >
              <div
                onClick={() => setPreviewCollapsed((c) => !c)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <span
                  style={{
                    color: "#94a3b8",
                    fontSize: "0.8rem",
                    transition: "transform .2s",
                    transform: previewCollapsed ? "rotate(-90deg)" : "none",
                  }}
                >
                  ▼
                </span>
                <div>
                  <h2
                    style={{
                      color: "#1a1a2e",
                      fontSize: "1rem",
                      margin: 0,
                    }}
                  >
                    Schedule Preview
                  </h2>
                  <p style={{ color: "#64748b", fontSize: "0.78rem", margin: 0 }}>
                    Live — updates as you build
                  </p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn
                  onClick={() => setPreviewModalOpen(true)}
                  variant="outline"
                  size="sm"
                >
                  Expand
                </Btn>
                <Btn
                  onClick={exportSchedulePDF}
                  size="sm"
                  disabled={!hasAtLeastOneTask}
                >
                  Export PDF
                </Btn>
              </div>
            </div>
            {!previewCollapsed && (
              <TimelinePreview
                days={days}
                speakers={speakers}
                previewDayIndex={previewDayIndex}
                onPreviewDayChange={setPreviewDayIndex}
                maxHeight={340}
              />
            )}
          </div>
        </div>

        {/* ══ SPEAKER MODAL ══ */}
        <Modal
          open={speakerModalOpen}
          onClose={() => setSpeakerModalOpen(false)}
          title="Select Speaker"
          maxWidth={780}
        >
          {speakers.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}
            >
              No approved speakers available.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: 12,
                marginBottom: 20,
              }}
            >
              {speakers.map((sp) => (
                <div
                  key={sp.id}
                  onClick={() => setSelectedSpeakerId(sp.id)}
                  style={{
                    borderRadius: 5,
                    padding: 14,
                    cursor: "pointer",
                    transition: "all .2s",
                    border: `2px solid ${selectedSpeakerId === sp.id ? "#2D2856" : "#e2e8f0"}`,
                    background:
                      selectedSpeakerId === sp.id
                        ? "rgba(45,40,86,.05)"
                        : "#f8fafc",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 6,
                    }}
                  >
                    <Avatar
                      name={sp.name}
                      photoBase64={sp.profile_picture?.base64}
                      size={40}
                    />
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#1a1a2e",
                          fontSize: "0.9rem",
                        }}
                      >
                        {sp.name}
                      </div>
                      <div style={{ color: "#64748b", fontSize: "0.78rem" }}>
                        {sp.jobtitle}
                        {sp.company ? ` · ${sp.company}` : ""}
                      </div>
                    </div>
                  </div>
                  {getTalkTopic(sp) && (
                    <div
                      style={{
                        color: "#2D2856",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        marginTop: 6,
                        wordBreak: "break-word",
                      }}
                    >
                      💬 {getTalkTopic(sp)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              paddingTop: 14,
              borderTop: "2px solid #f1f5f9",
              flexWrap: "wrap",
            }}
          >
            <Btn onClick={() => setSpeakerModalOpen(false)} variant="secondary">
              Cancel
            </Btn>
            <Btn onClick={assignSpeaker} disabled={!selectedSpeakerId}>
              Assign Speaker
            </Btn>
          </div>
        </Modal>

        {/* ══ ROOM MODAL ══ */}
        <Modal
          open={roomModalOpen}
          onClose={() => setRoomModalOpen(false)}
          title="Select Room"
          maxWidth={520}
        >
          {rooms.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "30px", color: "#94a3b8" }}
            >
              No rooms yet. Add rooms in the Rooms panel first.
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 20,
              }}
            >
              {rooms.map((room) => {
                const target = currentDay?.schedule.find(
                  (a) => a.id === roomTargetActId,
                );
                const active = target?.room === room.name;
                return (
                  <div
                    key={room.id}
                    onClick={() => {
                      setActivityRoom(roomTargetActId, room.name);
                      setRoomModalOpen(false);
                    }}
                    style={{
                      padding: "8px 16px",
                      borderRadius: 50,
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "0.88rem",
                      border: `2px solid ${active ? "#2D2856" : "#a7f3d0"}`,
                      background: active ? "rgba(45,40,86,.05)" : "#ecfdf5",
                      color: active ? "#2D2856" : "#065f46",
                      transition: "all .2s",
                    }}
                  >
                    📍 {room.name}
                  </div>
                );
              })}
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              paddingTop: 14,
              borderTop: "2px solid #f1f5f9",
              flexWrap: "wrap",
            }}
          >
            <Btn onClick={() => setRoomModalOpen(false)} variant="secondary">
              Cancel
            </Btn>
            <Btn
              variant="outline"
              onClick={() => {
                setActivityRoom(roomTargetActId, null);
                setRoomModalOpen(false);
              }}
            >
              No Room
            </Btn>
          </div>
        </Modal>

        {/* ══ FULL PREVIEW MODAL ══ */}
        <Modal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          title="Full Schedule Preview"
          maxWidth={900}
        >
          <TimelinePreview
            days={days}
            speakers={speakers}
            previewDayIndex={previewDayIndex}
            onPreviewDayChange={setPreviewDayIndex}
            maxHeight="none"
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              paddingTop: 14,
              marginTop: 14,
              borderTop: "2px solid #f1f5f9",
              flexWrap: "wrap",
            }}
          >
            <Btn onClick={() => setPreviewModalOpen(false)} variant="secondary">
              Close
            </Btn>
            <Btn onClick={exportSchedulePDF} disabled={!hasAtLeastOneTask}>
              Export PDF
            </Btn>
          </div>
        </Modal>

        <Notification message={notification} />
      </div>
    </DashboardLayout>
  );
}